import { ChevronDown } from "lucide-react";
import { createContext, type ReactNode, useContext, useState } from "react";
import { SettingsGroup } from "./SettingsGroup";
import { Card, CardContent, CardHeader } from "./ui/card";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "./ui/collapsible";

export const ExpandedSettingsPanels = createContext(false);

interface CollapsibleSettingsPanelProps {
	title: ReactNode;
	description: ReactNode;
	icon: ReactNode;
	headerAction?: ReactNode;
	children: ReactNode;
}

export function CollapsibleSettingsPanel({
	title,
	description,
	icon,
	headerAction,
	children,
}: CollapsibleSettingsPanelProps) {
	const [isOpen, setIsOpen] = useState(false);
	const expanded = useContext(ExpandedSettingsPanels);
	if (expanded)
		return (
			<SettingsGroup
				header={
					<div className="flex flex-wrap items-center justify-between gap-3">
						<h3 className="flex items-center gap-2">
							{icon}
							{title}
						</h3>
						{headerAction}
					</div>
				}
				description={description}
			>
				{children}
			</SettingsGroup>
		);

	return (
		<Collapsible open={isOpen} onOpenChange={setIsOpen}>
			<Card className="gap-0">
				<CardHeader className="p-0">
					<div className="flex items-start gap-2">
						<CollapsibleTrigger asChild>
							<button
								type="button"
								className="flex min-w-0 flex-1 items-center justify-between gap-4 rounded-xl px-6 py-5 text-start outline-none transition-colors hover:bg-muted/55 focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-2 [&[data-state=open]>svg]:rotate-180"
							>
								<span className="min-w-0">
									<span className="flex items-center gap-2 font-semibold">
										{icon}
										{title}
									</span>
									<span className="mt-1.5 block text-sm font-normal text-muted-foreground">
										{description}
									</span>
								</span>
								<ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200" />
							</button>
						</CollapsibleTrigger>
						{headerAction ? (
							<div className="pe-6 pt-5" onClickCapture={() => setIsOpen(true)}>
								{headerAction}
							</div>
						) : null}
					</div>
				</CardHeader>
				<CollapsibleContent className="border-t">
					<CardContent className="pt-6">{children}</CardContent>
				</CollapsibleContent>
			</Card>
		</Collapsible>
	);
}
